const loads = new Map<string, Promise<void>>()

/**
 * Loads a third-party `<script>` once per `src`, caching the result. Rejects on the browser's
 * `error` event, where blocked requests land. Adopts a matching tag another part of the page
 * already added rather than injecting a duplicate that would re-run the vendor's bootstrap.
 */
export function loadExternalScript (src: string): Promise<void> {
  if (typeof document === 'undefined') return Promise.reject(new Error('no document'))
  const cached = loads.get(src)
  if (cached !== undefined) return cached

  // A tag this module never created can already carry this script on a shared document. The
  // vendor bootstrap scans and renders on its own load, and TrustpilotBadge polls for the
  // rendered iframe, so adopting the tag as resolved is enough -- and avoids a second bootstrap.
  if (document.querySelector(`script[src="${src}"]`) !== null) {
    const adopted = Promise.resolve()
    loads.set(src, adopted)
    return adopted
  }

  const load = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => {
      script.remove()
      reject(new Error(`failed to load ${src}`))
    })
    document.body.appendChild(script)
  })
  loads.set(src, load)
  // A rejection stays cached only until it settles: evicting it lets a later mount retry
  // after a transient failure instead of failing for the life of the page.
  load.catch(() => {
    if (loads.get(src) === load) loads.delete(src)
  })
  return load
}
